import { Alert } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'src/components/Dialog';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';

export interface SettingChange {
  label: string;
  oldValue: any;
  newValue: any;
  fieldPath: string;
}

interface SettingsPropagationDialogProps {
  open: boolean;
  onClose: () => void;
  changedSettings: SettingChange[];
  childPlatformCount: number;
  onSaveOnly: () => Promise<void>;
  onSaveAndSync: () => Promise<void>;
  isLoading?: boolean;
}

export const SettingsPropagationDialog: React.FC<
  SettingsPropagationDialogProps
> = ({
  open,
  onClose,
  changedSettings,
  childPlatformCount,
  onSaveOnly,
  onSaveAndSync,
  isLoading = false,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'settingsPropagation',
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveOnly = async () => {
    setIsSaving(true);
    try {
      await onSaveOnly();
      onClose();
    } catch (error) {
      // Error handling will be done by the parent component
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndSync = async () => {
    setIsSaving(true);
    try {
      await onSaveAndSync();
      onClose();
    } catch (error) {
      // Error handling will be done by the parent component
    } finally {
      setIsSaving(false);
    }
  };

  const formatValue = (value: any): string => {
    if (typeof value === 'boolean') {
      return value
        ? t('enabled', { defaultValue: 'Enabled' })
        : t('disabled', { defaultValue: 'Disabled' });
    }
    if (Array.isArray(value)) {
      return value.length > 0
        ? `${value.length} items`
        : t('none', { defaultValue: 'None' });
    }
    if (value === null || value === undefined || value === '') {
      return t('notSet', { defaultValue: 'Not set' });
    }
    return String(value);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('title', { defaultValue: 'Settings Propagation' })}
      maxWidth="md"
      fullWidth
      actions={[
        {
          label: t('saveOnly', { defaultValue: 'Save Only' }),
          onClick: handleSaveOnly,
          disabled: isSaving || isLoading,
          variant: 'bordered',
        },
        {
          label: t('saveAndSync', { defaultValue: 'Save & Sync' }),
          onClick: handleSaveAndSync,
          disabled: isSaving || isLoading,
          variant: 'prominent',
          color: 'primary',
        },
      ]}
    >
      <Stack spacing={3}>
        <Typography variant="body1">
          {t('description', {
            defaultValue:
              'The following settings will be saved and can optionally be synced to all child platforms:',
          })}
        </Typography>

        <Stack spacing={1}>
          {changedSettings.map((setting) => (
            <ListItem
              key={setting.fieldPath}
              primary={setting.label}
              secondary={formatValue(setting.newValue)}
            />
          ))}
        </Stack>

        <Alert severity="info">
          {t('childPlatformMessage', {
            defaultValue:
              'All child platforms ({{count}}) will receive these updates if you choose "Save & Sync".',
            count: childPlatformCount,
          })}
        </Alert>

        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t('saveOnly', { defaultValue: 'Save Only' })}:</strong>{' '}
            {t('saveOnlyDescription', {
              defaultValue:
                'Saves changes to this platform only. Child platforms will not be updated.',
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>
              {t('saveAndSync', { defaultValue: 'Save & Sync' })}:
            </strong>{' '}
            {t('saveAndSyncDescription', {
              defaultValue:
                'Saves changes and immediately propagates them to all child platforms.',
            })}
          </Typography>
        </Stack>
      </Stack>
    </Dialog>
  );
};

export default SettingsPropagationDialog;
